"use client";

import { useState, useMemo } from "react";
import { format, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useExtendCampaignDateMutation } from "@/services/campaign";
import { useToast } from "components/ui/use-toast";
import { getTranslatedErrorMessage } from "../utils/error-messages";
import {
  CampaignConflictModal,
  type CampaignConflictItem,
} from "./campaign-conflict-modal";
import { CampaignType } from "@/@types/campaign";

type ExtendableCampaignType =
  | CampaignType.PRO013
  | CampaignType.PRO014
  | CampaignType.PRO017;

interface ExtendCampaignModalProps {
  campaignCode: number;
  campaignType: ExtendableCampaignType | string;
  campaignDescription: string;
  currentEndDate: string | Date;
}

export function ExtendCampaignModal({
  campaignCode,
  campaignType,
  currentEndDate,
}: ExtendCampaignModalProps) {
  const { toast } = useToast();
  const { handleModal } = useDialogModal();
  const [extendCampaign, { isLoading }] = useExtendCampaignDateMutation();

  const currentEndDateIso = useMemo(
    () => format(new Date(currentEndDate), "yyyy-MM-dd"),
    [currentEndDate],
  );
  const minNewEndDate = useMemo(
    () => format(addDays(new Date(currentEndDateIso), 1), "yyyy-MM-dd"),
    [currentEndDateIso],
  );

  const [newEndDate, setNewEndDate] = useState<string>(minNewEndDate);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!newEndDate) {
      next.newEndDate = "Selecione a nova data de término";
    } else {
      const today = format(new Date(), "yyyy-MM-dd");
      if (newEndDate <= today) {
        next.newEndDate = "A nova data deve ser maior que hoje";
      } else if (newEndDate <= currentEndDateIso) {
        next.newEndDate = "A nova data deve ser maior que a data final atual";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const close = () =>
    handleModal({ isOpen: false, element: null, title: "", description: "" });

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const result = await extendCampaign({
        type: String(campaignType).toUpperCase() as ExtendableCampaignType,
        code: campaignCode,
        newEndDate,
      }).unwrap();

      const isNoop = result.data?.noop;
      toast({
        title: isNoop ? "Sem alterações" : "Sucesso!",
        description: isNoop
          ? "A nova data é igual à data atual — nada foi alterado."
          : `Campanha ${campaignType} #${campaignCode} prorrogada com sucesso.`,
        variant: "default",
      });
      close();
    } catch (err: unknown) {
      const typedErr = err as {
        status?: number;
        data?: {
          message?: string;
          messages?: string[];
          conflicts?: CampaignConflictItem[];
        };
      };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        return;
      }

      const message = getTranslatedErrorMessage(
        typedErr,
        "Erro ao prorrogar campanha. Tente novamente.",
      );

      toast({
        title: "Erro ao prorrogar",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1">
        <Label htmlFor="newEndDate" className="text-sm">
          Nova data de término
        </Label>
        <Input
          id="newEndDate"
          type="date"
          min={minNewEndDate}
          value={newEndDate}
          onChange={(e) => setNewEndDate(e.target.value)}
          disabled={isLoading}
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          Atual: {format(new Date(currentEndDateIso), "dd/MM/yyyy", { locale: ptBR })}
        </p>
        {errors.newEndDate && (
          <p className="text-sm text-destructive">{errors.newEndDate}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={close} disabled={isLoading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Prorrogando..." : "Prorrogar"}
        </Button>
      </div>

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={() => {
          setShowConflictModal(false);
          setConflicts([]);
        }}
        onGoBack={() => {
          setShowConflictModal(false);
          setConflicts([]);
        }}
      />
    </div>
  );
}
